import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { RiLockPasswordLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import { forgotPasswordSchema, resetPasswordSchema } from 'schemas/acesso';
import styles from 'styles/styles.module.css';

const fieldStyle = {
  borderRadius: 14,
  borderColor: '#d7e4ec',
  background: '#f9fbfc',
  padding: '0.85rem 1rem',
};

// TODO: separar envio de email e verificação de código. envio continua no modal,
// verificação em outra page que pacientes acessarão por um link no email.
export default function RecuperarSenhaModal({
  showResetPasswordModal,
  setShowResetPasswordModal,
  userType,
  title = 'Recuperar senha',
}) {
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendForm = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onTouched',
    defaultValues: { email: '', cpf: '' },
  });

  const resetForm = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onTouched',
    defaultValues: { cpf: '', code: '', newPassword: '', confirmNewPassword: '' },
  });

  const handleSendEmail = async ({ email, cpf }) => {
    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${userType}/send/forgot-password-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, cpf }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        toast.error(data.message || data.error || 'Erro ao enviar email.');
        return;
      }

      resetForm.setValue('cpf', cpf);
      setIsEmailSent(true);
      toast.success(data.message || 'Email enviado.');
    } catch (error) {
      toast.error(error.message || 'Erro ao enviar email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async ({ cpf, code, newPassword }) => {
    try {
      setIsSubmitting(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${userType}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cpf, code, newPassword }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        toast.error(data.message || data.error || 'Erro ao atualizar senha.');
        return;
      }

      toast.success(data.message || 'Senha atualizada.');
      handleClose();
    } catch (error) {
      toast.error(error.message || 'Erro ao atualizar senha.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    sendForm.reset();
    resetForm.reset();
    setIsEmailSent(false);
    setShowResetPasswordModal(false);
  };

  const sendErrors = sendForm.formState.errors;
  const resetErrors = resetForm.formState.errors;

  return (
    <Modal
      show={showResetPasswordModal}
      onHide={handleClose}
      dialogClassName="modal-dialog-centered"
      contentClassName={styles.modalRecoveryContent}
    >
      <Modal.Header closeButton className="border-0 pb-2 position-relative">
        <Modal.Title className="d-flex align-items-center gap-2 fw-semibold text-dark">
          <RiLockPasswordLine size={18} />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0 pb-0">
        {userType === 'paciente' && !isEmailSent && (
          <Alert variant="warning" className="mb-3">
            <strong>O primeiro cadastro é feito pela clínica.</strong> Esta tela não cria um
            cadastro novo. Ela só define a senha de quem já foi cadastrado na recepção, com o mesmo
            e-mail e CPF informados lá.
          </Alert>
        )}
        {!isEmailSent ? (
          <Form
            id="form-forgot-password"
            noValidate
            onSubmit={sendForm.handleSubmit(handleSendEmail)}
          >
            <Form.Group controlId="formBasicEmail" className="mb-3">
              <Form.Label className="fw-semibold text-dark">Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Seu email"
                isInvalid={Boolean(sendErrors.email)}
                style={fieldStyle}
                {...sendForm.register('email')}
              />
              <Form.Control.Feedback type="invalid">
                {sendErrors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formBasicCPF" className="mb-3">
              <Form.Label className="fw-semibold text-dark">CPF</Form.Label>
              <Form.Control
                type="text"
                placeholder="Seu CPF"
                isInvalid={Boolean(sendErrors.cpf)}
                style={fieldStyle}
                {...sendForm.register('cpf')}
              />
              <Form.Control.Feedback type="invalid">
                {sendErrors.cpf?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        ) : (
          <Form
            id="form-reset-password"
            noValidate
            onSubmit={resetForm.handleSubmit(handleVerifyCode)}
          >
            <Form.Group controlId="formResetCPF" className="mb-3">
              <Form.Label>CPF</Form.Label>
              <Form.Control
                type="text"
                placeholder="Seu CPF"
                isInvalid={Boolean(resetErrors.cpf)}
                {...resetForm.register('cpf')}
              />
              <Form.Control.Feedback type="invalid">
                {resetErrors.cpf?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formBasicCode" className="mb-3">
              <Form.Label>Código de verificação</Form.Label>
              <Form.Control
                type="text"
                placeholder="Código recebido por email"
                isInvalid={Boolean(resetErrors.code)}
                {...resetForm.register('code')}
              />
              <Form.Control.Feedback type="invalid">
                {resetErrors.code?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formBasicPassword" className="mb-3">
              <Form.Label>Nova senha</Form.Label>
              <Form.Control
                type="password"
                placeholder="Sua nova senha"
                isInvalid={Boolean(resetErrors.newPassword)}
                {...resetForm.register('newPassword')}
              />
              <Form.Control.Feedback type="invalid">
                {resetErrors.newPassword?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formBasicConfirmPassword" className="mb-3">
              <Form.Label className="fw-semibold text-dark">Confirmar senha</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirme sua senha"
                isInvalid={Boolean(resetErrors.confirmNewPassword)}
                style={fieldStyle}
                {...resetForm.register('confirmNewPassword')}
              />
              <Form.Control.Feedback type="invalid">
                {resetErrors.confirmNewPassword?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Alert variant="info">
              <strong>
                Você receberá um email com um código de verificação para criar sua nova senha.
              </strong>
            </Alert>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-1 gap-2 d-flex flex-column">
        <RecoveryActions
          formId={isEmailSent ? 'form-reset-password' : 'form-forgot-password'}
          submitLabel={isEmailSent ? 'Mudar senha' : 'Enviar código'}
          isSubmitting={isSubmitting}
          onClose={handleClose}
        />
      </Modal.Footer>
    </Modal>
  );
}

function RecoveryActions({ formId, submitLabel, isSubmitting, onClose }) {
  return (
    <>
      <Button
        type="submit"
        form={formId}
        className={`rounded-pill w-100 border-0 ${styles.cardBtn} ${styles.cardBtnBlue}`}
        style={{
          minHeight: '3rem',
          padding: '0.85rem 1rem',
          fontWeight: 700,
          color: '#ffffff',
        }}
        disabled={isSubmitting}
      >
        {submitLabel}
        {isSubmitting && <Spinner animation="border" size="sm" className="ms-2" />}
      </Button>
      <Button
        type="button"
        className={`rounded-pill w-100 border-0 ${styles.cardBtn}`}
        style={{
          background: '#c8dbf3',
          color: '#1d5c8a',
          padding: '0.85rem 1rem',
          fontWeight: 700,
        }}
        onClick={onClose}
      >
        Fechar
      </Button>
    </>
  );
}
